import { createContext, useContext } from 'react'
import { SPOListFilterParamExtended } from '@frontend/components/ShipmentList/SPOListTable'
import { ApiClient, initApiClient } from '@frontend/lib/request'

import { ShipmentState, ShipmentStatus } from '@shared/const'

import { BulkShipmentsUpdate, ShipmentBulkUpdate } from '@shared-types/bulk_actions'
import { BookingTemplateShipment } from '@shared-types/calendar'
import { DeletedItem, DeletedShipmentPurchaseOrder } from '@shared-types/domain'
import { ShipmentPurchaseOrderSummary } from '@shared-types/purchase_orders'
import {
  ManifestGenerationResult,
  SenderShipmentManifestGenerationResult,
} from '@shared-types/purchase_orders/shipment-manifest-generation-types'
import {
  AttachmentV2,
  AttachmentV2CreatePayload,
  AttachmentV2UpdatePayload,
  EnhancedShipment,
  PurchaseOrderAggregatedResultTotals,
  ReplacePlannedShippingUnitsPayload,
  ReplaceReceivedShippingUnitsPayload,
  RestrictedShipmentUpdatePayload,
  Shipment,
  ShipmentAnomaliesUpdatePayload,
  ShipmentDashboardSummary,
  ShipmentPurchaseOrder,
  ShipmentPurchaseOrderCreationPayload,
  ShipmentPurchaseOrderStatusUpdatePayload,
  ShipmentStatusUpdatePayload,
  ShipmentSummaryAggregatedResult,
  ShipmentUpdatePayload,
  ShipmentUpdateRampPayload,
  ShipmentV2CreationPayload,
  ShippingUnitDetail,
  SMSNotificationPayload,
  SmsResult,
  SPOReplacePayload,
  UIListShipmentAggregatedQueryFilter,
  UIListShipmentSummariesQueryFilter,
} from '@shared-types/shipments'
import { AnyShipmentAuditEvent, ShipmentAuditEventType } from '@shared-types/shipments/audit_event'
import { BulkUpdateResult, Collection, Identifier, NanoId } from '@shared-types/util'

export interface ShipmentsClient {
  listShipmentSummaries: (
    shipmentSummaryParams: UIListShipmentSummariesQueryFilter
  ) => Promise<Collection<ShipmentDashboardSummary>>
  listSPOSummaries: (
    filters: SPOListFilterParamExtended
  ) => Promise<Collection<ShipmentPurchaseOrderSummary>>
  sposAggregated: (
    filters: SPOListFilterParamExtended
  ) => Promise<PurchaseOrderAggregatedResultTotals>
  getById: (_id: string) => Promise<Shipment>
  getDetails: (nanoId: NanoId) => Promise<EnhancedShipment>
  createV2: (payload: ShipmentV2CreationPayload) => Promise<Shipment>
  addFileAttachmentsV2: (
    nid: NanoId,
    attachments: AttachmentV2CreatePayload[]
  ) => Promise<Collection<AttachmentV2>>
  updateFileAttachmentsV2: (
    nid: NanoId,
    attachmentId: Identifier,
    data: AttachmentV2UpdatePayload
  ) => Promise<AttachmentV2>
  deleteFileAttachmentV2: (nid: NanoId, attachmentId: Identifier) => Promise<{ _id: Identifier }>
  replacePlannedShippingUnits: (
    nid: NanoId,
    payload: ReplacePlannedShippingUnitsPayload
  ) => Promise<Collection<ShippingUnitDetail[]>>
  replaceReceivedShippingUnits: (
    nid: NanoId,
    payload: ReplaceReceivedShippingUnitsPayload
  ) => Promise<Collection<ShippingUnitDetail[]>>
  listShipmentAuditEvents: (
    nid: NanoId,
    types: ShipmentAuditEventType[]
  ) => Promise<AnyShipmentAuditEvent[]>
  update: (shipmentId: NanoId, payload: ShipmentUpdatePayload) => Promise<Shipment>
  resetCheckIn: (shipmentId: NanoId) => Promise<Shipment>
  updateStatus: (
    shipmentId: NanoId,
    payload: ShipmentStatusUpdatePayload
  ) => Promise<{ shipment_status: ShipmentStatus }>
  updateSPOStatus: (
    shipmentId: NanoId,
    purchaseOrderId: Identifier,
    payload: ShipmentPurchaseOrderStatusUpdatePayload
  ) => Promise<ShipmentPurchaseOrder>
  replaceShipmentPurchaseOrders: (
    nid: NanoId,
    payload: SPOReplacePayload
  ) => Promise<Collection<ShipmentPurchaseOrder>>
  replaceShipmentPurchaseOrder: (
    nid: NanoId,
    purchaseOrderId: string,
    payload: ShipmentPurchaseOrderCreationPayload
  ) => Promise<ShipmentPurchaseOrder>
  deleteShipmentPurchaseOrder: (
    nid: NanoId,
    purchaseOrderId: string
  ) => Promise<DeletedShipmentPurchaseOrder>
  archive: (nid: NanoId) => Promise<Shipment>
  restore: (nid: NanoId) => Promise<Shipment>

  generateShipmentManifestPOs: (
    companyId: Identifier,
    payload: FormData
  ) => Promise<ManifestGenerationResult>

  getShipmentSecretToken: (shipmentNid: NanoId) => Promise<string>
  restrictedUpdate: (
    token: string,
    payload: RestrictedShipmentUpdatePayload
  ) => Promise<BookingTemplateShipment>
  listShipmentSummariesAggregated: (
    filter: UIListShipmentAggregatedQueryFilter
  ) => Promise<ShipmentSummaryAggregatedResult>
  generateSenders: (
    receivingCompanyId: string,
    senderCompanyIds: string[]
  ) => Promise<SenderShipmentManifestGenerationResult>
  assignRamp: (
    facilityId: Identifier,
    nid: NanoId,
    payload: ShipmentUpdateRampPayload
  ) => Promise<Shipment>
  smsNotifications: (shipmentNid: NanoId, payload: SMSNotificationPayload) => Promise<SmsResult>
  bulkUpdate: (payload: BulkShipmentsUpdate) => Promise<{
    results: BulkUpdateResult<ShipmentBulkUpdate>[]
  }>
  delete: (nid: NanoId) => Promise<DeletedItem>
  updateAnomalies: (nid: NanoId, payload: ShipmentAnomaliesUpdatePayload) => Promise<Shipment>
}

class DefaultShipmentsClient implements ShipmentsClient {
  private client: ApiClient

  constructor() {
    this.client = initApiClient({})
  }

  async createV2(payload: ShipmentV2CreationPayload) {
    return this.client.post('/v2/shipments', payload).then((data) => data.data)
  }

  async all() {
    return this.client.get('/shipments').then((res) => res.data.results)
  }

  async update(nid: NanoId, payload: ShipmentUpdatePayload) {
    return this.client.patch(`/shipments/${nid}`, payload).then((data) => data.data)
  }

  async resetCheckIn(nid: NanoId) {
    return this.client.delete(`/shipments/${nid}/check_in`).then((data) => data.data)
  }

  async restrictedUpdate(token: string, payload: RestrictedShipmentUpdatePayload) {
    return this.client.patch(`/restricted_shipments/${token}`, payload).then((data) => data.data)
  }

  async updateStatus(nid: NanoId, payload: ShipmentStatusUpdatePayload) {
    return this.client.put(`/shipments/${nid}/shipment_status`, payload).then((data) => data.data)
  }

  async updateSPOStatus(
    nid: NanoId,
    purchaseOrderId: Identifier,
    payload: ShipmentPurchaseOrderStatusUpdatePayload
  ) {
    return this.client
      .put(`/shipments/${nid}/shipment_purchase_orders/${purchaseOrderId}/status`, payload)
      .then((data) => data.data)
  }

  async listShipmentSummaries(params: UIListShipmentSummariesQueryFilter) {
    return this.client.get(`/shipment_summaries`, { params }).then((res) => res.data)
  }

  async listSPOSummaries(params: SPOListFilterParamExtended) {
    return this.client.get(`/shipment_purchase_orders`, { params }).then((res) => res.data)
  }

  async sposAggregated(params: SPOListFilterParamExtended) {
    return this.client
      .get(`/shipment_purchase_orders/_aggregated`, { params })
      .then((res) => res.data)
  }

  async getById(_id: string) {
    return this.client.get('/shipments/' + _id).then(({ data }) => data)
  }

  async getDetails(nanoId: NanoId) {
    return this.client.get(`/shipments/${nanoId}/details`).then(({ data }) => data)
  }

  async addFileAttachmentsV2(nid: NanoId, attachments: AttachmentV2CreatePayload[]) {
    return this.client
      .post(`/shipments/${nid}/attachments_v2`, { attachments })
      .then((res) => res.data)
  }

  async updateFileAttachmentsV2(
    nid: NanoId,
    attachmentId: Identifier,
    data: AttachmentV2UpdatePayload
  ) {
    return this.client
      .patch(`/shipments/${nid}/attachments_v2/${attachmentId}`, data)
      .then(({ data }) => data)
  }

  async deleteFileAttachmentV2(nid: NanoId, attachmentId: Identifier) {
    return this.client
      .delete(`shipments/${nid}/attachments_v2/${attachmentId}`)
      .then(({ data }) => data)
  }

  async replacePlannedShippingUnits(nid: NanoId, payload: ReplacePlannedShippingUnitsPayload) {
    return this.client
      .put(`shipments/${nid}/planned_shipping_units`, payload)
      .then(({ data }) => data)
  }

  async replaceReceivedShippingUnits(nid: NanoId, payload: ReplaceReceivedShippingUnitsPayload) {
    return this.client
      .put(`shipments/${nid}/received_shipping_units`, payload)
      .then(({ data }) => data)
  }

  async listShipmentAuditEvents(nid: NanoId, types: ShipmentAuditEventType[]) {
    const params = {
      types: types.length >= 1 ? types.toString() : undefined,
    }
    return this.client
      .get(`shipments/${nid}/audit_events`, { params })
      .then(({ data }) => data.results)
  }

  async replaceShipmentPurchaseOrders(nid: NanoId, payload: SPOReplacePayload) {
    return this.client
      .put(`shipments/${nid}/shipment_purchase_orders`, payload)
      .then(({ data }) => data)
  }

  async archive(nid: NanoId) {
    const updatePayload: ShipmentUpdatePayload = {
      state: ShipmentState.INACTIVE,
    }

    return this.client.patch(`shipments/${nid}`, updatePayload).then(({ data }) => data)
  }

  async restore(nid: NanoId) {
    const updatePayload: ShipmentUpdatePayload = {
      state: ShipmentState.ACTIVE,
    }

    return this.client.patch(`shipments/${nid}`, updatePayload).then(({ data }) => data)
  }

  async generateShipmentManifestPOs(companyId: string, payload: FormData) {
    return this.client
      .post(`companies/${companyId}/shipment_manifest/generate`, payload)
      .then(({ data }) => data)
  }

  async getShipmentSecretToken(shipmentNid: NanoId) {
    return this.client.get(`shipments/${shipmentNid}/secret_token`).then(({ data }) => data.token)
  }

  async replaceShipmentPurchaseOrder(
    nid: NanoId,
    purchaseOrderId: string,
    payload: ShipmentPurchaseOrderCreationPayload
  ) {
    return this.client
      .put(`shipments/${nid}/shipment_purchase_orders/${purchaseOrderId}`, payload)
      .then(({ data }) => data)
  }

  async deleteShipmentPurchaseOrder(nid: NanoId, purchaseOrderId: string) {
    return this.client
      .delete(`shipments/${nid}/shipment_purchase_orders/${purchaseOrderId}`)
      .then(({ data }) => data)
  }

  async listShipmentSummariesAggregated(filter: UIListShipmentAggregatedQueryFilter) {
    return this.client
      .get(`shipments/_aggregated`, {
        params: filter,
      })
      .then(({ data }) => data)
  }

  async generateSenders(receivingCompanyId: string, senderCompanyIds: string[]) {
    return this.client
      .post(`companies/${receivingCompanyId}/shipment_manifest/generate_senders`, {
        sending_company_id: senderCompanyIds,
      })
      .then(({ data }) => data)
  }

  assignRamp: (
    facilityId: Identifier,
    nid: NanoId,
    payload: ShipmentUpdateRampPayload
  ) => Promise<Shipment> = (
    facilityId: Identifier,
    nid: NanoId,
    payload: ShipmentUpdateRampPayload
  ) => {
    return this.client
      .put(`facilities/${facilityId}/shipments/${nid}/assign_ramp`, payload)
      .then(({ data }) => data)
  }

  smsNotifications(shipmentNid: NanoId, payload: SMSNotificationPayload) {
    return this.client
      .post(`shipments/${shipmentNid}/sms_notifications`, payload)
      .then(({ data }) => data)
  }

  async bulkUpdate(payload: BulkShipmentsUpdate) {
    return this.client.patch(`shipments/bulk_update`, payload).then(({ data }) => data)
  }

  async delete(nid: NanoId) {
    return this.client.delete(`shipments/${nid}`).then(({ data }) => data)
  }

  async updateAnomalies(nid: NanoId, payload: ShipmentAnomaliesUpdatePayload) {
    return this.client.put(`shipments/${nid}/anomalies`, payload).then(({ data }) => data)
  }
}

const ShipmentsClientContext = createContext<ShipmentsClient>(new DefaultShipmentsClient())

export function useShipmentsClient() {
  return useContext(ShipmentsClientContext)
}
